import React, { useEffect, useState } from 'react';
import FaiBox from '../../MuiComponents/FaiBox';
import FaiTypography from "../../MuiComponents/FaiTypography";
import theme from "../../assets/theme";

// icons
import orders from "../../assets/images/icons/orders.svg";
import shoppingCart from "../../assets/images/icons/shopping_cart.svg";
import house from "../../assets/images/icons/house.svg"
import math from "../../assets/images/icons/math.svg"
import rupee from "../../assets/images/icons/rupee.svg"

import {Grid, CircularProgress, TextField, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Button} from "@mui/material";
import { toast } from "react-toastify";

// component
import Card from "./components/Card";
import PaymentController from '../../controllers/paymentController';
import OrdersController from '../../controllers/orderController';
import { PaymentOverviewData, PaymentCalculationSummaryData } from '../../types/paymentTypes';
import { format, addMonths, subMonths } from 'date-fns';

function Payments() {
    const {typography, borders, palette} = theme;
    const {borderRadius} = borders;
    
    const [loading, setLoading] = useState(true);
    const [paymentOverview, setPaymentOverview] = useState<PaymentOverviewData | null>(null);
    const [calculationSummary, setCalculationSummary] = useState<PaymentCalculationSummaryData | null>(null);
    const [dashboardSummary, setDashboardSummary] = useState<any>(null);
    const [payouts, setPayouts] = useState<any[]>([]);
    const [settlements, setSettlements] = useState<any[]>([]);
    const [currentMonth, setCurrentMonth] = useState(format(new Date(), 'yyyy-MM'));

    // ₹ with 2 decimals; "-" for null/undefined.
    const inr = (v: any) =>
        v == null ? '-' : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // Open the settlement's GST tax-invoice PDF (the payout calculation receipt).
    const downloadSettlementInvoice = async (id: string) => {
        try {
            const res = await PaymentController.getMerchantSettlementInvoice(id);
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        } catch (error) {
            toast.error('No receipt available for this settlement');
        }
    };
    
    // Generate last 12 months for dropdown
    const generateMonthOptions = () => {
        const options = [];
        const currentDate = new Date();
        
        for (let i = 0; i < 12; i++) {
            const date = subMonths(currentDate, i);
            const value = format(date, 'yyyy-MM');
            const label = format(date, 'MMMM yyyy');
            options.push({ value, label });
        }
        
        return options;
    };
    
    const monthOptions = generateMonthOptions();

    const formatMonthYear = (dateString: string) => {
        try {
            const date = new Date(`${dateString}-01`);
            if (isNaN(date.getTime())) {
                return format(new Date(), 'MMMM yyyy');
            }
            return format(date, 'MMMM yyyy');
        } catch (error) {
            console.error('Error formatting date:', error);
            return format(new Date(), 'MMMM yyyy');
        }
    };

    useEffect(() => {
        const fetchPaymentData = async () => {
            try {
                setLoading(true);
                console.log(`Fetching payment data for month: ${currentMonth}`);
                
                const [overviewResponse, calculationResponse, dashboardResponse, payoutsResponse, settlementsResponse] = await Promise.all([
                    PaymentController.getPaymentOverview(currentMonth),
                    PaymentController.getPaymentCalculationSummary(currentMonth),
                    OrdersController.getMerchantDashboardSummary(),
                    PaymentController.getMerchantPayouts(0, 100),
                    PaymentController.getMerchantSettlements(0, 50)
                ]);

                setPaymentOverview(overviewResponse.data.data);
                setCalculationSummary(calculationResponse.data.data);
                setDashboardSummary(dashboardResponse.data.data);
                // ResponseDTO -> GetDataListWithPaginationResponseDTO -> data list.
                setPayouts(payoutsResponse?.data?.data?.data || []);
                setSettlements(settlementsResponse?.data?.data?.data || []);
            } catch (error) {
                console.error('Error fetching payment data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPaymentData();
    }, [currentMonth]);

    // Handle month change
    const handleMonthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newMonth = event.target.value;
        console.log(`Month changed from ${currentMonth} to ${newMonth}`);
        setCurrentMonth(newMonth);
    };

    // Order totals by period — real data from the merchant dashboard summary.
    const orderCountFooter = (count: number) => (
        <FaiTypography component={"span"} variant={"caption"} color={"inherit"}>
            {count} order{count === 1 ? '' : 's'}
        </FaiTypography>
    );
    const paymentCard = [
        {
            bgColor: "#1AB595",
            icon: orders,
            title: "Today's Order",
            amount: dashboardSummary?.today?.amount ?? 0,
            footer: orderCountFooter(dashboardSummary?.today?.count ?? 0),
        }, {
            bgColor: "#3190D0",
            icon: orders,
            title: "Yesterday's Order",
            amount: dashboardSummary?.yesterday?.amount ?? 0,
            footer: orderCountFooter(dashboardSummary?.yesterday?.count ?? 0),
        }, {
            bgColor: "#10459E",
            icon: shoppingCart,
            title: "This month's Order",
            amount: dashboardSummary?.thisMonth?.amount ?? 0,
            footer: orderCountFooter(dashboardSummary?.thisMonth?.count ?? 0),
        }
    ]

    // Calculate percentage change text and formatting
    const getPercentageText = (percentage: number) => {
        if (percentage === 0) return '';
        
        const prefix = percentage > 0 ? '+' : '';
        return `${prefix}${percentage}% since last month`;
    };

    // Dynamic data for overall cards based on API response
    const overAllCards = [
        {
            bgColor: "#1AB595",
            icon: rupee,
            title: "Next Payment",
            value: paymentOverview?.nextPayment || 0,
            footer: <>
                    <FaiTypography component={"span"} variant={"caption"} mr={1} color={"inherit"}>
                        {paymentOverview ? getPercentageText(paymentOverview.nextPaymentPercentageComparedToLastMonth) : ''}
                    </FaiTypography>
                </>

        }, {
            bgColor: "#FF6A6A",
            icon: math,
            title: "Outstanding Payment",
            value: paymentOverview?.outstandingDues || 0,
            footer: <>
                    <FaiTypography component={"span"} variant={"caption"} mr={1} color={"inherit"}>
                        {paymentOverview ? getPercentageText(paymentOverview.outstandingDuesPercentageComparedToLastMonth) : ''}
                    </FaiTypography>
                </>
        }, {
            bgColor: "#00682A",
            icon: house,
            title: "Payments Till Date",
            value: paymentOverview?.paymentTillDate || 0,
            footer: <>
                    <FaiTypography component={"span"} variant={"caption"} mr={1} color={"inherit"}>
                    </FaiTypography>
                </>
        }
    ]

    if (loading) {
        return (
            <FaiBox display="flex" justifyContent="center" alignItems="center" height="60vh">
                <CircularProgress />
            </FaiBox>
        );
    }

    return (
        <>
            <FaiBox>
                <FaiBox className={"payments"} mb={4}>
                    <FaiBox className={"payment-title"} display="flex" justifyContent="space-between" alignItems="center">
                        <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"21px"}>
                            Payments
                        </FaiTypography>
                        <TextField
                            select
                            value={currentMonth}
                            onChange={handleMonthChange}
                            label="Select Month"
                            variant="outlined"
                            size="small"
                            sx={{ 
                                minWidth: 200, 
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme.palette.primary.main,
                                        borderWidth: 2
                                    }
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: theme.palette.primary.main
                                }
                            }}
                        >
                            {monthOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </FaiBox>
                    <FaiBox>
                        <Grid container spacing={3}>
                            {paymentCard.map((data, index) => {
                                return (
                                    <Grid item sm={12} md={4} key={index}>
                                        <Card
                                            bgColor={data.bgColor}
                                            title={data.title}
                                            icon={data.icon}
                                            footer={data.footer}
                                            amount={data.amount}
                                        />
                                    </Grid>
                                )
                            })}
                        </Grid>
                    </FaiBox>
                </FaiBox>
                <FaiBox className={"details"} mb={3}>
                    <FaiBox className={"details-title"}>
                        <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"21px"}>
                            This Month
                        </FaiTypography>
                    </FaiBox>
                    <FaiBox bgColor={palette.white.main} borderRadius={borderRadius.md} p={3}>
                        <FaiBox>
                            <Grid container spacing={3}>
                                <Grid item sm={12} md={4}>
                                    <FaiBox p={2}>
                                        <FaiBox mb={4}>
                                            <FaiTypography variant={"h6"} textAlign={"center"}
                                                           fontWeight={typography.fontWeightMedium} lineHeight={1}>
                                                Total Orders Delivered
                                            </FaiTypography>
                                            <FaiBox textAlign={"center"} lineHeight={1}>
                                                <FaiTypography component={"span"} variant={"caption"}
                                                               textAlign={"center"}>
                                                    {formatMonthYear(currentMonth)}
                                                </FaiTypography>
                                            </FaiBox>
                                            <FaiTypography variant={"h4"} textAlign={"center"}
                                                           fontWeight={typography.fontWeightBold}>
                                                {calculationSummary?.deliveredOrdersCount || 0}
                                            </FaiTypography>
                                        </FaiBox>

                                        <FaiBox>
                                            <FaiBox display={"flex"} justifyContent={"space-between"}
                                                    color={palette.success.main}>
                                                <FaiTypography variant={"h6"} color={"inherit"}
                                                               fontWeight={typography.fontWeightMedium}>
                                                    Delivered
                                                </FaiTypography>
                                                <FaiTypography variant={"h6"}
                                                               fontWeight={typography.fontWeightMedium}>
                                                    {calculationSummary?.deliveredOrdersCount || 0}
                                                </FaiTypography>
                                            </FaiBox>
                                            <FaiBox display={"flex"} justifyContent={"space-between"}
                                                    color={palette.error.main}>
                                                <FaiTypography variant={"h6"} color={"inherit"}
                                                               fontWeight={typography.fontWeightMedium}>
                                                    Customer Returns
                                                </FaiTypography>
                                                <FaiTypography variant={"h6"}
                                                               fontWeight={typography.fontWeightMedium}>
                                                    {calculationSummary?.returnedOrdersCount || 0}
                                                </FaiTypography>
                                            </FaiBox>
                                        </FaiBox>
                                    </FaiBox>
                                </Grid>
                                <Grid item sm={12} md={8}>
                                    <FaiBox bgColor={"#D0FFCF"} borderRadius={borderRadius.md} p={2}>
                                        <FaiBox mb={2}>
                                            <FaiTypography variant={"h6"} textAlign={"center"}
                                                           fontWeight={typography.fontWeightMedium} lineHeight={1}>
                                                Payment Calculated
                                            </FaiTypography>
                                            <FaiBox textAlign={"center"} lineHeight={1}>
                                                <FaiTypography component={"span"} variant={"caption"}
                                                               textAlign={"center"}>
                                                    {calculationSummary ? `${calculationSummary.deliveredOrdersCount + calculationSummary.returnedOrdersCount} Orders` : '0 Orders'}
                                                </FaiTypography>
                                            </FaiBox>
                                            <FaiTypography variant={"h4"} textAlign={"center"}
                                                           fontWeight={typography.fontWeightBold}>
                                                Rs. {calculationSummary ? (calculationSummary.deliveredOrdersAmount + calculationSummary.returnedOrdersAmount).toLocaleString() : '0'}
                                            </FaiTypography>
                                        </FaiBox>
                                        <FaiBox>
                                            <Grid container spacing={0}>
                                                <Grid item sm={12} md={6}>
                                                    <FaiBox borderRight={"1px solid"}>
                                                        <FaiTypography variant={"h6"} textAlign={"center"}
                                                                       fontWeight={typography.fontWeightMedium}>
                                                            Paid
                                                        </FaiTypography>
                                                        <FaiBox textAlign={"center"}>
                                                            <FaiTypography component={"span"} variant={"caption"}
                                                                           textAlign={"center"}>
                                                                {calculationSummary?.deliveredOrdersCount || 0} Orders
                                                            </FaiTypography>
                                                        </FaiBox>
                                                        <FaiTypography variant={"h4"} textAlign={"center"}
                                                                       fontWeight={typography.fontWeightBold}>
                                                            Rs. {calculationSummary?.deliveredOrdersAmount.toLocaleString() || '0'}
                                                        </FaiTypography>
                                                    </FaiBox>
                                                </Grid>
                                                <Grid item sm={12} md={6}>
                                                    <FaiBox>
                                                        <FaiTypography variant={"h6"} textAlign={"center"}
                                                                       fontWeight={typography.fontWeightMedium}>
                                                            Pending
                                                        </FaiTypography>
                                                        <FaiBox textAlign={"center"}>
                                                            <FaiTypography component={"span"} variant={"caption"}
                                                                           textAlign={"center"}>
                                                                {calculationSummary?.returnedOrdersCount || 0} Orders
                                                            </FaiTypography>
                                                        </FaiBox>
                                                        <FaiTypography variant={"h4"} textAlign={"center"}
                                                                       fontWeight={typography.fontWeightBold}>
                                                            Rs. {calculationSummary?.returnedOrdersAmount.toLocaleString() || '0'}
                                                        </FaiTypography>
                                                    </FaiBox>
                                                </Grid>
                                            </Grid>
                                        </FaiBox>
                                    </FaiBox>
                                </Grid>
                                <Grid item sm={12} md={4}>
                                    <FaiBox p={2} sx={{
                                        borderRadius: borderRadius.md,
                                        backgroundColor: "#C5E7FE",
                                        border: "10px solid #A9DCFF"
                                    }}>
                                        <FaiTypography variant={"h6"} textAlign={"center"}
                                                       fontWeight={typography.fontWeightMedium}>
                                            Next Payment
                                        </FaiTypography>
                                        <FaiTypography variant={"h4"} textAlign={"center"}
                                                       fontWeight={typography.fontWeightBold}>
                                            Rs. {paymentOverview?.nextPayment.toLocaleString() || '0'}
                                        </FaiTypography>
                                    </FaiBox>
                                </Grid>
                                <Grid item sm={12} md={8}>
                                    <FaiBox p={2} sx={{
                                        borderRadius: borderRadius.md,
                                        backgroundColor: "#FFDEDE",
                                        border: "10px solid #F9CCCC"
                                    }}>
                                        <FaiTypography variant={"h6"} textAlign={"center"}
                                                       fontWeight={typography.fontWeightMedium}>
                                            Outstanding Payment
                                        </FaiTypography>
                                        <FaiTypography variant={"h4"} textAlign={"center"}
                                                       fontWeight={typography.fontWeightBold}>
                                            Rs. {paymentOverview?.outstandingDues.toLocaleString() || '0'}
                                        </FaiTypography>
                                    </FaiBox>
                                </Grid>
                            </Grid>
                        </FaiBox>
                    </FaiBox>
                </FaiBox>

                <FaiBox className={"overall"}>
                    <FaiBox className={"overall-title"}>
                        <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"21px"}>
                            Overall
                        </FaiTypography>
                    </FaiBox>
                    <FaiBox>
                        <Grid container spacing={3}>
                            {overAllCards.map((data, index) => (
                                <Grid item sm={12} md={4} key={index}>
                                    <Card 
                                        bgColor={data.bgColor} 
                                        title={data.title} 
                                        icon={data.icon}
                                        footer={data.footer}
                                        amount={data.value}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </FaiBox>
                </FaiBox>

                <FaiBox className={"payouts"} mt={4}>
                    <FaiBox className={"payouts-title"}>
                        <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"21px"}>
                            Payouts
                        </FaiTypography>
                    </FaiBox>
                    <FaiBox bgColor={palette.white.main} borderRadius={borderRadius.md} p={2}>
                        {payouts.length === 0 ? (
                            <FaiBox textAlign={"center"} p={3}>
                                <FaiTypography variant={"body1"}>
                                    No payouts yet. Payouts are created when your orders are confirmed and are paid out after delivery.
                                </FaiTypography>
                            </FaiBox>
                        ) : (
                            <TableContainer>
                                <Table size={"small"}>
                                    {/* The global MuiTableHead theme override sets
                                        display:block, which detaches header columns from
                                        the body (headers bunch left, values drift right).
                                        Restore the default so columns align. */}
                                    <TableHead sx={{ display: "table-header-group" }}>
                                        <TableRow>
                                            <TableCell>Order</TableCell>
                                            <TableCell align={"right"}>Gross</TableCell>
                                            <TableCell align={"right"}>Commission</TableCell>
                                            <TableCell align={"right"}>Net Payable</TableCell>
                                            <TableCell align={"center"}>Status</TableCell>
                                            <TableCell>Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {payouts.map((p: any) => {
                                            const status = (p.status || '').toUpperCase();
                                            const color =
                                                status === 'PROCESSED' ? 'success'
                                                : status === 'WITHHELD' ? 'error'
                                                : status === 'ELIGIBLE_FOR_PAYOUT' ? 'info'
                                                : status === 'PENDING' ? 'warning'
                                                : 'default';
                                            return (
                                                <TableRow key={p.id}>
                                                    <TableCell>{p.razorpayOrderId || p.subOrderId || '-'}</TableCell>
                                                    <TableCell align={"right"}>₹{p.grossAmount ?? 0}</TableCell>
                                                    <TableCell align={"right"}>₹{p.commissionAmount ?? 0}</TableCell>
                                                    <TableCell align={"right"}>₹{p.netAmount ?? 0}</TableCell>
                                                    <TableCell align={"center"}>
                                                        <Chip label={p.status || '-'} color={color as any} size={"small"}/>
                                                    </TableCell>
                                                    <TableCell>{p.createdAt ? String(p.createdAt).slice(0, 10) : '-'}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </FaiBox>
                </FaiBox>

                <FaiBox className={"settlements"} mt={4}>
                    <FaiBox className={"settlements-title"}>
                        <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"6px"}>
                            Settlement Receipts
                        </FaiTypography>
                        <FaiTypography variant={"caption"} color={palette.text.main} mb={"21px"} display={"block"}>
                            One statement per settlement run. Commission is GST-inclusive (net = gross − commission);
                            the receipt breaks out the 18% GST. Download the tax invoice for your records.
                        </FaiTypography>
                    </FaiBox>
                    <FaiBox bgColor={palette.white.main} borderRadius={borderRadius.md} p={2}>
                        {settlements.length === 0 ? (
                            <FaiBox textAlign={"center"} p={3}>
                                <FaiTypography variant={"body1"}>
                                    No settlements yet. A settlement receipt is generated each payout run for your
                                    delivered orders past the return window.
                                </FaiTypography>
                            </FaiBox>
                        ) : (
                            <TableContainer>
                                <Table size={"small"}>
                                    {/* Same fix as the Payouts table: undo the global
                                        display:block on TableHead so columns align. */}
                                    <TableHead sx={{ display: "table-header-group" }}>
                                        <TableRow>
                                            <TableCell>Period</TableCell>
                                            <TableCell align={"right"}>Gross Sales</TableCell>
                                            <TableCell align={"right"}>Commission (incl GST)</TableCell>
                                            <TableCell align={"right"}>of which GST</TableCell>
                                            <TableCell align={"right"}>Returns</TableCell>
                                            <TableCell align={"right"}>Net Payout</TableCell>
                                            <TableCell align={"center"}>Status</TableCell>
                                            <TableCell align={"center"}>Receipt</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {settlements.map((s: any) => {
                                            const status = (s.status || '').toUpperCase();
                                            const color =
                                                status === 'PROCESSED' ? 'success'
                                                : status === 'FAILED' ? 'error'
                                                : status === 'CARRIED_FORWARD' ? 'warning'
                                                : status === 'PENDING' ? 'warning'
                                                : 'default';
                                            return (
                                                <TableRow key={s.id}>
                                                    <TableCell>{s.settledForPeriod || '-'}</TableCell>
                                                    <TableCell align={"right"}>{inr(s.grossSales)}</TableCell>
                                                    <TableCell align={"right"}>{inr(s.totalCommissionInclGst)}</TableCell>
                                                    <TableCell align={"right"}>{inr(s.gstOnCommission)}</TableCell>
                                                    <TableCell align={"right"}>{inr(s.returnedAmount)}</TableCell>
                                                    <TableCell align={"right"}>
                                                        <strong>{inr(s.netPayout)}</strong>
                                                    </TableCell>
                                                    <TableCell align={"center"}>
                                                        <Chip label={s.status || '-'} color={color as any} size={"small"}/>
                                                    </TableCell>
                                                    <TableCell align={"center"}>
                                                        {s.invoiceNumber ? (
                                                            <Button size={"small"} onClick={() => downloadSettlementInvoice(s.id)}>
                                                                Download
                                                            </Button>
                                                        ) : (
                                                            <FaiTypography component={"span"} variant={"caption"} color={palette.text.main}>
                                                                —
                                                            </FaiTypography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </FaiBox>
                </FaiBox>
            </FaiBox>
        </>
    );
}

export default Payments;
